import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import Loader from "../../template/view.loader";
import { Plus, Trash2, Edit2, Calendar, MapPin, Users } from "lucide-react";

import AlertModal from "../../../components/ui/AlertModal";
import moment from "moment";

export default function ReunionList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = useSelector((state) => state.userReducer?.token);
    
    const [reunions, setReunions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

    const ecoleId = user?.ecole?._id || user?.ecole;

    const loadReunions = () => {
        setLoading(true);
        handleGet(`/reunion/ecole/${ecoleId}`, (res) => {
            if (res.state === "success") {
                setReunions(res.reunions);
            }
            setLoading(false);
        }, token);
    };

    useEffect(() => {
        loadReunions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmDelete = async () => {
        if (!deleteModal.id) return;
        await handleRemove(`/reunion/${deleteModal.id}`, null, token, { skipConfirm: true });
        
        loadReunions();
        setDeleteModal({ isOpen: false, id: null });
    };

    const openDeleteModal = (id) => {
        setDeleteModal({ isOpen: true, id });
    };

    if (loading) return <Loader />;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Réunions</h1>
                    <p className="text-gray-500">Gérez les réunions administratives et les convocations</p>
                </div>
                <Link to="/reunions/create" className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-white font-medium rounded-lg hover:bg-indigo-700 transition shadow-sm">
                    <Plus className="w-4 h-4" /> Nouvelle Réunion
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reunions.length === 0 ? (
                    <div className="col-span-full bg-white p-8 rounded-xl border border-gray-200 text-center text-gray-500">
                        Aucune réunion n'est prévue pour le moment.
                    </div>
                ) : (
                    reunions.map((reunion) => {
                        const date = moment(reunion.date).format("DD/MM/YYYY");
                        return (
                            <div key={reunion._id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition flex flex-col h-full">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-semibold text-lg text-gray-900 line-clamp-2">{reunion.titre}</h3>
                                </div>
                                <div className="space-y-3 mb-6 flex-1">
                                    <div className="flex items-center text-gray-600 text-sm">
                                        <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
                                        <span>{date} de {reunion.heure_debut} à {reunion.heure_fin}</span>
                                    </div>
                                    <div className="flex items-center text-gray-600 text-sm">
                                        <MapPin className="w-4 h-4 mr-2 text-rose-500" />
                                        <span className="line-clamp-1">{reunion.lieu_lien}</span>
                                    </div>
                                    <div className="flex items-center text-gray-600 text-sm">
                                        <Users className="w-4 h-4 mr-2 text-green-500" />
                                        <span>
                                            {reunion.participants_internes?.length || 0} internes, {reunion.participants_externes?.length || 0} externes
                                        </span>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-auto border-t pt-4">
                                    <Link to={`/reunions/update/${reunion._id}`} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Modifier">
                                        <Edit2 className="w-4 h-4" />
                                    </Link>
                                    <button onClick={() => openDeleteModal(reunion._id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Supprimer">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <AlertModal 
                isOpen={deleteModal.isOpen} 
                title="Supprimer la réunion" 
                message="Voulez-vous vraiment supprimer cette réunion ? Cette action est irréversible." 
                onConfirm={confirmDelete} 
                onCancel={() => setDeleteModal({ isOpen: false, id: null })} 
                type="danger" 
            />
        </div>
    );
}
